import { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Headphones, 
  Clock, 
  User, 
  Mail, 
  Phone, 
  MessageSquare, 
  Send, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  Bot,
  UserCircle,
  Volume2,
  VolumeX
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Status configurations
const STATUS_CONFIG = {
  waiting: {
    label: { ar: 'في الانتظار', en: 'Waiting' },
    color: 'bg-yellow-500',
    textColor: 'text-yellow-500',
    bgLight: 'bg-yellow-500/10',
    icon: Clock
  },
  in_progress: {
    label: { ar: 'قيد المعالجة', en: 'In Progress' },
    color: 'bg-blue-500',
    textColor: 'text-blue-500',
    bgLight: 'bg-blue-500/10',
    icon: MessageSquare
  },
  pending_callback: {
    label: { ar: 'بانتظار الرد', en: 'Pending Callback' },
    color: 'bg-orange-500',
    textColor: 'text-orange-500',
    bgLight: 'bg-orange-500/10',
    icon: AlertCircle
  },
  timeout: {
    label: { ar: 'انتهت المهلة', en: 'Timeout' },
    color: 'bg-red-500',
    textColor: 'text-red-500',
    bgLight: 'bg-red-500/10',
    icon: XCircle
  },
  resolved: {
    label: { ar: 'تم الحل', en: 'Resolved' },
    color: 'bg-green-500',
    textColor: 'text-green-500',
    bgLight: 'bg-green-500/10',
    icon: CheckCircle
  }
};

export default function AdminSupport() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastTicketCount, setLastTicketCount] = useState(0);
  const chatEndRef = useRef(null);
  const audioRef = useRef(null);

  const t = (ar, en) => language === 'ar' ? ar : en;

  // Get auth token
  const getToken = () => localStorage.getItem('business_token');

  // Fetch tickets
  const fetchTickets = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const url = filter === 'all' 
        ? `${BACKEND_URL}/api/admin/support/tickets`
        : `${BACKEND_URL}/api/admin/support/tickets?status=${filter}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      const newTickets = response.data.tickets || [];
      
      // Check for new tickets and play sound
      const waitingCount = newTickets.filter(t => t.status === 'waiting').length;
      if (waitingCount > lastTicketCount && soundEnabled && lastTicketCount > 0) {
        playNotificationSound();
      }
      setLastTicketCount(waitingCount);
      
      setTickets(newTickets);
      
      // Update selected ticket if it exists
      if (selectedTicket) {
        const updated = newTickets.find(t => t.ticket_id === selectedTicket.ticket_id);
        if (updated) setSelectedTicket(updated);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      if (showLoading) {
        toast.error(t('خطأ في جلب التذاكر', 'Error fetching tickets'));
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [filter, selectedTicket, lastTicketCount, soundEnabled]);

  // Play notification sound
  const playNotificationSound = () => {
    if (audioRef.current) {
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
  };

  // Auto-refresh every 10 seconds
  useEffect(() => {
    fetchTickets(true);
    const interval = setInterval(() => fetchTickets(false), 10000);
    return () => clearInterval(interval);
  }, [filter]);

  // Scroll to bottom of chat
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedTicket?.agent_messages, selectedTicket?.chat_history]);

  // Assign ticket to self
  const assignTicket = async (ticketId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/support/ticket/${ticketId}/assign`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success(t('تم تعيين التذكرة', 'Ticket assigned'));
      fetchTickets(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('خطأ في التعيين', 'Assignment error'));
    }
  };

  // Send reply
  const sendReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    
    setSendingReply(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/support/ticket/${selectedTicket.ticket_id}/reply`,
        { message: replyMessage },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setReplyMessage('');
      toast.success(t('تم إرسال الرد', 'Reply sent'));
      fetchTickets(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('خطأ في الإرسال', 'Send error'));
    } finally {
      setSendingReply(false);
    }
  };

  // Resolve ticket
  const resolveTicket = async () => {
    if (!selectedTicket) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/support/ticket/${selectedTicket.ticket_id}/resolve`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success(t('تم إغلاق التذكرة', 'Ticket resolved'));
      setSelectedTicket(null);
      fetchTickets(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('خطأ في الإغلاق', 'Resolve error'));
    }
  };

  // Format time ago
  const timeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return t('الآن', 'Just now');
    if (seconds < 3600) return t(`${Math.floor(seconds / 60)} دقيقة`, `${Math.floor(seconds / 60)}m ago`);
    if (seconds < 86400) return t(`${Math.floor(seconds / 3600)} ساعة`, `${Math.floor(seconds / 3600)}h ago`);
    return t(`${Math.floor(seconds / 86400)} يوم`, `${Math.floor(seconds / 86400)}d ago`);
  };

  // Filter tabs
  const filterTabs = [
    { key: 'all', label: t('الكل', 'All') },
    { key: 'waiting', label: t('في الانتظار', 'Waiting') },
    { key: 'in_progress', label: t('قيد المعالجة', 'In Progress') },
    { key: 'pending_callback', label: t('بانتظار الرد', 'Pending') }
  ];

  // Count tickets by status
  const waitingCount = tickets.filter(t => t.status === 'waiting').length;

  return (
    <div className={`min-h-screen cgm-page`}>
      {/* Hidden audio element for notifications */}
      <audio ref={audioRef} preload="auto">
        <source src="https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3" type="audio/mpeg" />
      </audio>

      {/* Header */}
      <div className={`sticky top-0 z-20 border-b ${isDarkMode ? 'bg-[#131B2C] border-gray-800' : 'bg-white border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-[#38BDF8]/20' : 'bg-[#38BDF8]/10'}`}>
                <Headphones className="h-6 w-6 text-[#38BDF8]" />
              </div>
              <div>
                <h1 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {t('إدارة الدعم الفني', 'Support Management')}
                </h1>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {t('الرد على استفسارات العملاء', 'Respond to customer inquiries')}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Waiting badge */}
              {waitingCount > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-yellow-500/20 text-yellow-500">
                  <Clock className="h-4 w-4" />
                  <span className="font-semibold">{waitingCount}</span>
                  <span className="text-sm">{t('في الانتظار', 'waiting')}</span>
                </div>
              )}
              
              {/* Sound toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSoundEnabled(!soundEnabled)}
                className={isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}
                data-testid="sound-toggle"
              >
                {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
              </Button>
              
              {/* Refresh */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fetchTickets(true)}
                disabled={loading}
                className={isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}
                data-testid="refresh-btn"
              >
                <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
          
          {/* Filter tabs */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-1">
            {filterTabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  filter === tab.key
                    ? 'bg-[#38BDF8] text-white'
                    : isDarkMode
                      ? 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`filter-${tab.key}`}
              >
                {tab.label}
                {tab.key === 'waiting' && waitingCount > 0 && (
                  <span className="ms-2 px-1.5 py-0.5 rounded-full bg-white/20 text-xs">
                    {waitingCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tickets list */}
          <div className={`lg:col-span-1 rounded-2xl overflow-hidden ${isDarkMode ? 'bg-[#131B2C]' : 'bg-white'} shadow-lg`}>
            <div className={`p-4 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
              <h2 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('التذاكر', 'Tickets')} ({tickets.length})
              </h2>
            </div>
            
            <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
              {loading && tickets.length === 0 ? (
                <div className="p-8 text-center">
                  <RefreshCw className={`h-8 w-8 mx-auto mb-2 animate-spin ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                  <p className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>{t('جاري التحميل...', 'Loading...')}</p>
                </div>
              ) : tickets.length === 0 ? (
                <div className="p-8 text-center">
                  <Headphones className={`h-12 w-12 mx-auto mb-3 ${isDarkMode ? 'text-gray-700' : 'text-gray-300'}`} />
                  <p className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>{t('لا توجد تذاكر', 'No tickets')}</p>
                </div>
              ) : (
                tickets.map(ticket => {
                  const statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.waiting;
                  const StatusIcon = statusConfig.icon;
                  const isSelected = selectedTicket?.ticket_id === ticket.ticket_id;
                  
                  return (
                    <button
                      key={ticket.ticket_id}
                      onClick={() => setSelectedTicket(ticket)}
                      className={`w-full p-4 text-start border-b transition-all ${
                        isDarkMode ? 'border-gray-800' : 'border-gray-100'
                      } ${
                        isSelected
                          ? isDarkMode ? 'bg-[#38BDF8]/10' : 'bg-[#38BDF8]/5'
                          : isDarkMode ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'
                      }`}
                      data-testid={`ticket-${ticket.ticket_id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${statusConfig.bgLight}`}>
                          <StatusIcon className={`h-4 w-4 ${statusConfig.textColor}`} />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className={`font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {ticket.user_name || t('عميل', 'Customer')}
                            </span>
                            <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {timeAgo(ticket.created_at)}
                            </span>
                          </div>
                          
                          <p className={`text-sm truncate mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {ticket.message || ticket.inquiry_message || t('استفسار جديد', 'New inquiry')}
                          </p>
                          
                          <div className="flex items-center gap-2 mt-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${statusConfig.bgLight} ${statusConfig.textColor}`}>
                              {statusConfig.label[language]}
                            </span>
                            {ticket.assigned_to_name && (
                              <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                • {ticket.assigned_to_name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Ticket details */}
          <div className={`lg:col-span-2 rounded-2xl overflow-hidden ${isDarkMode ? 'bg-[#131B2C]' : 'bg-white'} shadow-lg`}>
            {selectedTicket ? (
              <div className="flex flex-col h-[calc(100vh-220px)]">
                {/* Ticket header */}
                <div className={`p-4 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setSelectedTicket(null)}
                        className={`lg:hidden p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
                      >
                        <ChevronLeft className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                      </button>
                      
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {selectedTicket.user_name || t('عميل', 'Customer')}
                          </h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            STATUS_CONFIG[selectedTicket.status]?.bgLight || 'bg-gray-100'
                          } ${STATUS_CONFIG[selectedTicket.status]?.textColor || 'text-gray-500'}`}>
                            {STATUS_CONFIG[selectedTicket.status]?.label[language] || selectedTicket.status}
                          </span>
                        </div>
                        
                        <div className={`flex items-center gap-4 mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {selectedTicket.user_email && (
                            <span className="flex items-center gap-1">
                              <Mail className="h-3.5 w-3.5" />
                              {selectedTicket.user_email}
                            </span>
                          )}
                          {selectedTicket.user_phone && (
                            <span className="flex items-center gap-1" dir="ltr">
                              <Phone className="h-3.5 w-3.5" />
                              {selectedTicket.user_phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {selectedTicket.status === 'waiting' && (
                        <Button
                          onClick={() => assignTicket(selectedTicket.ticket_id)}
                          className="bg-[#38BDF8] hover:bg-[#38BDF8]/90 text-white"
                          data-testid="assign-btn"
                        >
                          {t('تعيين لي', 'Assign to me')}
                        </Button>
                      )}
                      
                      {(selectedTicket.status === 'in_progress' || selectedTicket.status === 'pending_callback') && (
                        <Button
                          onClick={resolveTicket}
                          variant="outline"
                          className={isDarkMode ? 'border-green-500 text-green-500 hover:bg-green-500/10' : 'border-green-600 text-green-600 hover:bg-green-50'}
                          data-testid="resolve-btn"
                        >
                          <CheckCircle className="h-4 w-4 me-2" />
                          {t('إغلاق التذكرة', 'Resolve')}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Chat messages */}
                <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${isDarkMode ? 'bg-[#0B0F19]/50' : 'bg-gray-50/50'}`}>
                  {/* Initial inquiry */}
                  {selectedTicket.inquiry_message && (
                    <div className="flex justify-end">
                      <div className={`max-w-[80%] rounded-2xl rounded-tr-sm p-4 ${
                        isDarkMode ? 'bg-[#38BDF8]/20 text-white' : 'bg-[#38BDF8]/10 text-gray-900'
                      }`}>
                        <div className="flex items-center gap-2 mb-1">
                          <User className="h-3.5 w-3.5" />
                          <span className="text-xs font-medium">{t('استفسار العميل', 'Customer Inquiry')}</span>
                        </div>
                        <p className="text-sm">{selectedTicket.inquiry_message}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* AI Chat history */}
                  {selectedTicket.chat_history?.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] rounded-2xl p-4 ${
                        msg.role === 'user'
                          ? isDarkMode ? 'bg-[#38BDF8]/20 text-white rounded-tr-sm' : 'bg-[#38BDF8]/10 text-gray-900 rounded-tr-sm'
                          : isDarkMode ? 'bg-gray-800 text-gray-200 rounded-tl-sm' : 'bg-white text-gray-800 rounded-tl-sm shadow-sm'
                      }`}>
                        <div className="flex items-center gap-2 mb-1">
                          {msg.role === 'user' ? (
                            <UserCircle className="h-3.5 w-3.5" />
                          ) : (
                            <Bot className="h-3.5 w-3.5 text-[#38BDF8]" />
                          )}
                          <span className="text-xs font-medium">
                            {msg.role === 'user' ? t('العميل', 'Customer') : 'CGM AI'}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </div>
                  ))}

                  {/* Agent messages */}
                  {selectedTicket.agent_messages?.map((msg, idx) => (
                    <div key={msg.id || idx} className="flex justify-start">
                      <div className={`max-w-[80%] rounded-2xl rounded-tl-sm p-4 ${
                        isDarkMode ? 'bg-green-500/20 text-white' : 'bg-green-50 text-gray-900'
                      }`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Headphones className="h-3.5 w-3.5 text-green-500" />
                          <span className="text-xs font-medium text-green-500">
                            {msg.sender_name || t('الدعم الفني', 'Support')}
                          </span>
                          <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {msg.timestamp ? timeAgo(msg.timestamp) : ''}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </div>
                  ))}
                  
                  <div ref={chatEndRef} />
                </div>

                {/* Reply input */}
                {(selectedTicket.status === 'in_progress' || selectedTicket.status === 'pending_callback') && (
                  <div className={`p-4 border-t ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
                    <div className="flex gap-3">
                      <Input
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        placeholder={t('اكتب ردك هنا...', 'Type your reply...')}
                        className={`flex-1 ${
                          isDarkMode 
                            ? 'bg-[#1E293B] border-gray-700 text-white placeholder:text-gray-500'
                            : 'bg-white border-gray-200 text-gray-900'
                        }`}
                        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendReply()}
                        data-testid="reply-input"
                      />
                      <Button
                        onClick={sendReply}
                        disabled={!replyMessage.trim() || sendingReply}
                        className="bg-[#38BDF8] hover:bg-[#38BDF8]/90 text-white px-6"
                        data-testid="send-reply-btn"
                      >
                        {sendingReply ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Resolved state */}
                {selectedTicket.status === 'resolved' && (
                  <div className={`p-4 border-t ${isDarkMode ? 'border-gray-800 bg-green-500/10' : 'border-gray-100 bg-green-50'}`}>
                    <div className="flex items-center justify-center gap-2 text-green-500">
                      <CheckCircle className="h-5 w-5" />
                      <span className="font-medium">{t('تم إغلاق هذه التذكرة', 'This ticket has been resolved')}</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[calc(100vh-220px)] p-8">
                <div className={`p-6 rounded-full mb-4 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                  <MessageSquare className={`h-12 w-12 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                </div>
                <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {t('اختر تذكرة للعرض', 'Select a ticket to view')}
                </h3>
                <p className={`text-sm text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {t('اختر تذكرة من القائمة للرد على استفسارات العملاء', 'Choose a ticket from the list to respond to customer inquiries')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
